import React, { useState } from "react";
import { Link2, Loader2, MapPin, Check, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import MapLocationPicker, { GeocodedLocationData } from "./MapLocationPicker";
import { AddPatientAddressPayload, PatientAddress } from "./types";
import { AddressService } from "./address.service";

interface GoogleLinkAddressFormProps {
  patientId: string;
  onAddressCreated: (address: PatientAddress) => void;
  onCancel: () => void;
}

export const GoogleLinkAddressForm: React.FC<GoogleLinkAddressFormProps> = ({
  patientId,
  onAddressCreated,
  onCancel,
}) => {
  const [mapUrl, setMapUrl] = useState("");
  const [parsing, setParsing] = useState(false);
  const [locationData, setLocationData] = useState<GeocodedLocationData | null>(null);

  // Form Fields
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [addressName, setAddressName] = useState("Flat");
  const [landmark, setLandmark] = useState("");
  const [saving, setSaving] = useState(false);

  // Extract coordinates from Google Maps link
  const parseGoogleMapsLink = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error("Please paste a valid Google Maps URL");
      return;
    }

    try {
      setParsing(true);
      let lat: number | null = null;
      let lng: number | null = null;

      // Regex pattern 1: @lat,lng e.g. @26.915975,80.948318
      const atMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (atMatch) {
        lat = parseFloat(atMatch[1]);
        lng = parseFloat(atMatch[2]);
      }

      // Regex pattern 2: ?q=lat,lng or ll=lat,lng
      if (!lat || !lng) {
        const qMatch = trimmed.match(/[?&](?:q|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (qMatch) {
          lat = parseFloat(qMatch[1]);
          lng = parseFloat(qMatch[2]);
        }
      }

      // Regex pattern 3: search/lat,lng or place/lat,lng
      if (!lat || !lng) {
        const pathMatch = trimmed.match(/(?:place|search)\/(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (pathMatch) {
          lat = parseFloat(pathMatch[1]);
          lng = parseFloat(pathMatch[2]);
        }
      }

      if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
        setLocationData({
          lat,
          lng,
          address: "",
          city: "",
          state: "",
          pincode: "",
        });
        toast.success("Location extracted! Adjust pin or verify address below.");
      } else {
        // Default center fallback if shortlink or unparsed URL
        toast.error("Could not parse coordinates directly. Opening map location selector.");
        setLocationData({
          lat: 26.8467,
          lng: 80.9462,
          address: "",
          city: "",
          state: "",
          pincode: "",
        });
      }
    } catch (err) {
      console.error("Link parse error:", err);
      toast.error("Failed to parse link. Please select location manually on the map.");
    } finally {
      setParsing(false);
    }
  };

  const handleLocationSelect = (data: GeocodedLocationData) => {
    setLocationData(data);
    if (data.address && !address) setAddress(data.address);
    if (data.city) setCity(data.city);
    if (data.state) setState(data.state);
    if (data.pincode) setPincode(data.pincode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!locationData) {
      toast.error("Please select a location on the map");
      return;
    }
    if (!address.trim()) {
      toast.error("Address is required");
      return;
    }
    if (!pincode.trim()) {
      toast.error("Pincode is required");
      return;
    }

    const payload: AddPatientAddressPayload = {
      patientId,
      address: address.trim(),
      city: city.trim() || "Lucknow",
      state: state.trim() || "Uttar Pradesh",
      pincode: pincode.trim(),
      latitude: locationData.lat,
      longitude: locationData.lng,
      isDefault: false,
      addressName: addressName.trim() || "Flat",
      landmark: landmark.trim(),
    };

    try {
      setSaving(true);
      const res = await AddressService.addPatientAddress(payload);
      if (res.success && res.data) {
        toast.success("Address added successfully!");
        onAddressCreated(res.data);
      } else {
        // Fallback object if backend doesn't return full address wrapper
        const newAddress: PatientAddress = {
          id: res.data?.id || Date.now(),
          patientId,
          address: payload.address,
          city: payload.city,
          state: payload.state,
          pincode: payload.pincode,
          latitude: payload.latitude,
          longitude: payload.longitude,
          addressName: payload.addressName,
          landmark: payload.landmark,
        };
        toast.success(res.message || "Address saved!");
        onAddressCreated(newAddress);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
      {/* 1. Paste Google Maps Link Bar */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold text-slate-700 flex items-center gap-1">
          <Link2 size={14} className="text-emerald-600" />
          Paste Google Maps Link
        </label>
        <div className="flex items-center gap-2">
          <input
            type="url"
            value={mapUrl}
            onChange={(e) => setMapUrl(e.target.value)}
            placeholder="e.g. https://maps.google.com/?q=26.9159,80.9483 or https://goo.gl/maps/..."
            className="flex-1 text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800 placeholder:text-slate-400"
          />
          <button
            type="button"
            onClick={() => parseGoogleMapsLink(mapUrl)}
            disabled={parsing || !mapUrl.trim()}
            className="px-3.5 py-2.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1"
          >
            {parsing ? <Loader2 size={13} className="animate-spin" /> : "Load Location"}
          </button>
        </div>
      </div>

      {/* 2. Interactive Map */}
      <div className="flex flex-col gap-1">
        <label className="text-[0.7rem] font-bold text-slate-500 uppercase tracking-wide">
          Verify & Adjust Pin Location on Map
        </label>
        <MapLocationPicker
          initialCenter={locationData ? { lat: locationData.lat, lng: locationData.lng } : null}
          onLocationSelect={handleLocationSelect}
        />
      </div>

      {/* 3. Address Detail Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
        <div className="md:col-span-2">
          <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
            Full Address <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street address, house number, area"
            required
            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
          />
        </div>

        <div>
          <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">City</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
          />
        </div>

        <div>
          <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">State</label>
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="State"
            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
          />
        </div>

        <div>
          <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">
            Pincode <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
            placeholder="6-digit Pincode"
            required
            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800"
          />
        </div>

        <div>
          <label className="text-[0.7rem] font-bold text-slate-700 block mb-1">Tag (Address Name)</label>
          <select
            value={addressName}
            onChange={(e) => setAddressName(e.target.value)}
            className="w-full text-xs p-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:outline-none text-slate-800 bg-white"
          >
            <option value="Home">Home</option>
            <option value="Work">Work</option>
            <option value="Flat">Flat / Apartment</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !address.trim() || !pincode.trim()}
          className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5 shadow-xs"
        >
          {saving ? (
            <>
              <Loader2 size={13} className="animate-spin" /> Saving Address...
            </>
          ) : (
            <>
              <Check size={14} /> Save & Select Address
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default GoogleLinkAddressForm;
